import { useEffect, useState } from "react";
import api from "../lib/api";
import { NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Wallet } from "lucide-react";

/**
 * Sidebar chip — polls `/partner-payments/pending` for the Founder and
 * bumps the count instantly whenever a `partner_payment` notification
 * arrives via websocket (so the Founder never has to refresh Sales to
 * see a fresh partner deposit waiting to be verified).
 */
export default function PendingPaymentsChip() {
  const { user, notifications } = useAuth();
  const [count, setCount] = useState(0);
  const role = user?.real_role || user?.effective_role;
  const canSee = role === "FOUNDER" || role === "BDOM";

  const refetch = async () => {
    if (!canSee) return;
    try {
      const { data } = await api.get("/partner-payments/pending");
      setCount((data || []).length);
    } catch { /* silent */ }
  };

  // Initial fetch + slow safety poll (10 min) in case a webhook is missed.
  useEffect(() => {
    if (!canSee) return;
    refetch();
    const id = setInterval(refetch, 10 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee, user?.id]);

  // Websocket-driven refresh — any incoming `partner_payment` kind means
  // a partner just logged a payment (single OR bulk CSV). Refetch the
  // count so the chip bumps within a second of the deposit.
  useEffect(() => {
    if (!canSee || !notifications?.length) return;
    const latest = notifications[0];
    if (latest?.kind === "partner_payment") refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications?.[0]?.id]);

  if (!canSee || count === 0) return null;
  return (
    <NavLink to="/pending-payments" data-testid="pending-payments-chip"
      className="mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition group">
      <div className="flex items-center gap-2 min-w-0">
        <Wallet size={14} className="text-amber-700 shrink-0"/>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">Pending</div>
          <div className="text-xs font-semibold text-amber-900 truncate">Partner payments</div>
        </div>
      </div>
      <span data-testid="pending-payments-count" className="text-sm font-bold text-white bg-amber-600 rounded-full min-w-[24px] h-6 px-2 flex items-center justify-center shrink-0">
        {count}
      </span>
    </NavLink>
  );
}
